import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getPointsByConfirmedPercent } from '../utils/assignmentPoints';
import { assignmentSubmissionImageCache } from '../utils/assignmentSubmissionImageCache';
import AssignmentSubmissionCropModal from './AssignmentSubmissionCropModal';

interface StudentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    status: string,
    points: number,
    memo: string,
    submissionData?: {
      link: string;
      message: string;
      isDeleted: boolean;
      isImageDeleted?: boolean;
      replacementImage?: { blob: Blob; mimeType: string };
    }
  ) => void;
  studentName: string;
  studentNumber: string;
  currentStatus: string;
  currentPoints: number;
  currentMemo: string;
  assignmentTitle: string;
  assignmentType: 'assignment' | 'project' | 'routine';
  projectTitle?: string; // 프로젝트 제목
  subTaskTitle?: string; // 프로젝트 하위 과제 제목
  routineTitle?: string; // 루틴 과제 제목
  dateId?: string; // 루틴 과제 날짜
  onNavigate?: (
    direction: 'prev' | 'next',
    currentState?: {
      status: string;
      points: number;
      memo: string;
      submissionLink?: string;
      submissionMessage?: string;
      isDeleted?: boolean;
      isImageDeleted?: boolean;
    }
  ) => void;
  canNavigatePrev?: boolean; // 이전으로 이동 가능한지
  canNavigateNext?: boolean; // 다음으로 이동 가능한지
  defaultPoints?: number; // 환경설정의 기본 단위 포인트
  statusDefaultPoints?: {
    pending: number;
    confirmed: number;
    delayed: number;
    exempted: number;
  }; // 상태별 기본 포인트
  assignmentBasePoints?: number; // 과제 설정 포인트
  submissionLink?: string; // 멤버가 제출한 링크
  submissionMessage?: string; // 멤버가 남긴 메시지
  submittedAt?: Date; // 제출 시간
  submissionImagePath?: string;
  submissionImageUpdatedAt?: string;
  initialIsDeleted?: boolean; // 초기 삭제 예정 상태
  initialIsImageDeleted?: boolean;
  /** 과제 체크 모달(z-index 1300+) 위에 올릴 때 true */
  elevatedStack?: boolean;
  /** 스탠다드 플랜 이상에서만 제출 확인 섹션 표시 */
  submissionFeaturesEnabled?: boolean;
}

/** AssignmentCheckModal Content와 동일한 가로 규격 */
const DETAIL_MODAL_MAX_WIDTH = 680;
const DETAIL_MODAL_OVERLAY_PADDING_X = 20;
const DETAIL_MODAL_OVERLAY_PADDING_Y = 40;

// 과제 체크 모달의 멤버별 기록 편집 모달과 완전히 동일한 스타일
const Overlay = styled.div<{ $z: number }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${(p) => p.$z};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 100%;
  box-sizing: border-box;
`;

const OverlayCloseButton = styled.button`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const OverlayScroll = styled.div`
  min-height: 100%;
  box-sizing: border-box;
  padding: ${DETAIL_MODAL_OVERLAY_PADDING_Y}px ${DETAIL_MODAL_OVERLAY_PADDING_X}px;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 768px) {
    padding: 20px 10px;
  }
`;

const backdropNavZoneWidth = `calc((100vw - min(${DETAIL_MODAL_MAX_WIDTH}px, 100vw - ${DETAIL_MODAL_OVERLAY_PADDING_X * 2}px)) / 2)`;
const backdropNavZoneWidthMobile = `calc((100vw - min(${DETAIL_MODAL_MAX_WIDTH}px, 100vw - 20px)) / 2)`;

const BackdropNavZoneShell = styled.div<{ $side: 'left' | 'right' }>`
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: 1;
  width: ${backdropNavZoneWidth};
  margin: 0;
  padding: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$side === 'left' ? 'flex-end' : 'flex-start')};
  ${(p) => (p.$side === 'left' ? 'left: 0;' : 'right: 0;')}
  ${(p) =>
    p.$side === 'left'
      ? 'padding-right: clamp(12px, 4vw, 32px);'
      : 'padding-left: clamp(12px, 4vw, 32px);'}
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: ${backdropNavZoneWidthMobile};
  }
`;

const BackdropNavHitArea = styled.button<{ $side: 'left' | 'right' }>`
  width: 100%;
  height: 100%;
  border: none;
  margin: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$side === 'left' ? 'flex-end' : 'flex-start')};
  touch-action: pan-y;

  & svg {
    opacity: 0;
    transition: opacity 0.2s ease;
    flex-shrink: 0;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.35));
  }

  &:hover:not(:disabled) svg {
    opacity: 1;
  }

  &:disabled {
    cursor: default;
    pointer-events: none;
  }

  &:focus-visible:not(:disabled) {
    outline: 2px solid rgba(255, 255, 255, 0.8);
    outline-offset: -4px;
  }
`;

const Container = styled.div<{ $z: number }>`
  position: relative;
  flex-shrink: 0;
  margin: auto;
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: ${(p) => p.$z};
  width: 100%;
  max-width: ${DETAIL_MODAL_MAX_WIDTH}px;
  max-height: none;
  overflow: visible;
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--color-border);
  position: relative;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: left;
  flex: 1;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
`;

const NavigationContainer = styled.div`
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
`;

const Navigation = styled.div`
  display: flex;
  gap: 8px;
`;

const NavButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Content = styled.div`
  padding: 20px 24px;
`;

const AssignmentInfo = styled.div`
  margin-bottom: 20px;
`;

const AssignmentTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 4px;
`;

const SubInfo = styled.div`
  font-size: 14px;
  color: #6c757d;
  font-weight: 400;
`;

const SubmissionImageRow = styled.div`
  margin-bottom: 12px;
`;

const SubmissionImagePreviewWrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

const SubmissionDeleteOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  pointer-events: none;
  z-index: 1;
`;

const SubmissionContentWrap = styled.div`
  position: relative;
  width: 100%;
`;

const SubmissionImageEditBlock = styled.div`
  margin-bottom: 12px;
`;

const SubmissionImagePickBtn = styled.button`
  width: 100%;
  padding: 10px 14px;
  margin-top: 8px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    color: var(--color-text-primary);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  :root[data-theme='light'] & {
    background: #ffffff;
  }
`;

const SubmissionImagePreview = styled.img`
  width: 100%;
  height: auto;
  display: block;
  vertical-align: top;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-panel);
`;

const SubmissionImagePlaceholder = styled.div`
  width: 100%;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px dashed var(--color-border);
  background: var(--color-panel);
  font-size: 13px;
  color: var(--color-text-secondary);
`;

const SubmissionEmptyImageSlot = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px dashed var(--color-border);
  background: var(--color-panel);
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  text-align: center;
  padding: 12px;
  box-sizing: border-box;
`;

const SubmissionEmptyLinkSlot = styled.div`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
  border-radius: 8px;
  border: 1px dashed var(--color-border);
  background: var(--color-surface);
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  text-align: center;
  padding: 12px 16px;
  box-sizing: border-box;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
`;

const StatusButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  justify-content: center;
  width: 100%;
  height: 32px;
  min-height: 32px;
  align-items: center;
  
  @media (max-width: 420px) {
    height: 28px;
    min-height: 28px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
    flex-wrap: nowrap;
    height: 28px;
    min-height: 28px;
  }
  
  @media (max-width: 380px) {
    gap: 2px;
    flex-wrap: nowrap;
  }
  
  @media (max-width: 350px) {
    gap: 1px;
    flex-wrap: nowrap;
  }
`;

const StatusButton = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 12px;
  background: var(--color-surface);
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  height: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 420px) {
    justify-content: center;
    padding: 4px;
    height: 28px;
    min-width: 24px;
  }
`;

const StatusButtonColorDot = styled.div<{ $isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-weight: 700;
  font-size: 12px;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  flex-shrink: 0;
  
  @media (max-width: 420px) {
    margin-right: 0;
    width: 20px;
    height: 20px;
    font-size: 12px;
  }
`;

const StatusButtonText = styled.div<{ $isSelected: boolean }>`
  font-size: 14px;
  font-weight: ${props => props.$isSelected ? '600' : '400'};
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }

  @media (max-width: 420px) {
    display: none;
  }

  @media (max-width: 380px) {
    display: none;
  }

  @media (max-width: 350px) {
    display: none;
  }
`;

const BatchInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  padding-top: 8px;
  margin-bottom: 8px;
`;

const BatchLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-secondary);
  text-align: center;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 2px 6px;
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  white-space: nowrap;
`;

const BatchInput = styled.input`
  padding: 8px 4px;
  padding-left: 4px;
  padding-right: 4px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-weight: normal;
  width: 100%;
  min-width: 0;
  text-align: center;
  height: 38px;
  line-height: 38px;
  box-sizing: border-box;
  font-family: inherit;
  background: var(--color-category-input-bg);
  color: var(--color-text-primary);
  transition: all 0.15s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const PointsMinusBtn = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const PointsPlusBtn = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const MemoEditTextarea = styled.textarea`
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  background: var(--color-category-input-bg);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-border);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 16px 24px 20px;
  border-top: 1px solid var(--color-border);
`;

const Button = styled.button`
  padding: 10px 20px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  color: #6c757d;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background: var(--color-accent-strong);
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-accent);
  }
`;

const SubmissionSection = styled.div`
  width: 100%;
  margin-bottom: 20px;
  background: rgba(var(--color-accent-rgb), 0.1);
  border-radius: 12px;
  padding: 16px;
`;

const SubmissionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  position: relative;
`;

const SubmissionTitleWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const SubmissionSectionTitle = styled.div`
  font-size: 13px;
  font-weight: bold;
  color: var(--color-text-primary);
  text-align: center;
  background: var(--color-surface)fff;
  border: 2px solid var(--color-accent-strong);
  border-radius: 20px;
  padding: 4px 16px;
  width: fit-content;
`;

const SubmissionActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  position: absolute;
  right: 0;
`;

const SubmissionActionButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-panel);
    color: ${props => props.title === '삭제' ? '#dc3545' : '#007bff'};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SubmissionLinkButton = styled.a`
  display: block;
  width: 100%;
  padding: 12px 16px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  word-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.6;
  min-height: 44px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SubmissionMessageButton = styled.div`
  display: block;
  width: 100%;
  padding: 12px 16px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  cursor: default;
  transition: all 0.2s ease;
  word-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.6;
  min-height: 44px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SubmissionButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const LinkIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
  }
`;

const SubmissionEditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SubmissionEditInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: rgba(var(--color-accent-rgb), 0.5);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }
`;

const SubmissionEditTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: rgba(var(--color-accent-rgb), 0.5);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }
`;

const SubmissionEditActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const SubmissionEditButton = styled.button<{ $primary?: boolean }>`
  padding: 6px 16px;
  border: 1px solid ${props => props.$primary ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 6px;
  background: ${props => props.$primary ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$primary ? 'white' : 'var(--color-text-primary)'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$primary ? 'rgba(var(--color-accent-rgb), 0.9)' : 'var(--color-panel)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentDetailModal: React.FC<StudentDetailModalProps> = ({
  isOpen,
  onClose,
  onSave,
  studentName,
  studentNumber,
  currentStatus,
  currentPoints,
  currentMemo,
  assignmentTitle,
  assignmentType,
  projectTitle,
  subTaskTitle,
  routineTitle,
  dateId,
  onNavigate,
  canNavigatePrev = false,
  canNavigateNext = false,
  defaultPoints = 1,
  statusDefaultPoints,
  assignmentBasePoints,
  submissionLink,
  submissionMessage,
  submittedAt,
  submissionImagePath,
  submissionImageUpdatedAt,
  initialIsDeleted = false,
  initialIsImageDeleted = false,
  elevatedStack = false,
  submissionFeaturesEnabled = false
}) => {
  const overlayZ = elevatedStack ? 1400 : 1200;
  const containerZ = elevatedStack ? 1401 : 1201;
  const [status, setStatus] = useState(currentStatus);
  const [points, setPoints] = useState(currentPoints);
  const [isForcedPoints, setIsForcedPoints] = useState(false);
  const [memo, setMemo] = useState(currentMemo);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  // 메모는 '학생이 바뀔 때'만 props로 동기화 (키보드로 상태 선택 시 메모 필드 유지)
  const lastSyncedMemoRef = useRef<string>(currentMemo);

  // 제출 확인 데이터 상태 관리
  const [editedSubmissionLink, setEditedSubmissionLink] = useState(submissionLink || '');
  const [editedSubmissionMessage, setEditedSubmissionMessage] = useState(submissionMessage || '');
  const [isSubmissionDeleted, setIsSubmissionDeleted] = useState(
    initialIsDeleted || initialIsImageDeleted
  );
  const [isEditingSubmission, setIsEditingSubmission] = useState(false);
  const [submissionImageUrl, setSubmissionImageUrl] = useState<string | null>(null);
  const [submissionImageLoadFailed, setSubmissionImageLoadFailed] = useState(false);
  const [pendingReplacementImage, setPendingReplacementImage] = useState<{
    blob: Blob;
    mimeType: string;
    previewUrl: string;
  } | null>(null);
  const [isSubmissionCropOpen, setIsSubmissionCropOpen] = useState(false);

  useEffect(() => {
    setStatus(currentStatus);
    setPoints(currentPoints);
    setIsForcedPoints(false);
    setEditedSubmissionLink(submissionLink || '');
    setEditedSubmissionMessage(submissionMessage || '');
    setIsSubmissionDeleted(initialIsDeleted || initialIsImageDeleted);
    setIsEditingSubmission(false);
    if (pendingReplacementImage?.previewUrl) {
      URL.revokeObjectURL(pendingReplacementImage.previewUrl);
    }
    setPendingReplacementImage(null);
    // 메모: 학생이 바뀐 경우에만 props로 덮어씀 (키보드로 상태만 바꿀 때는 메모 유지)
    if (currentMemo !== lastSyncedMemoRef.current) {
      setMemo(currentMemo);
      lastSyncedMemoRef.current = currentMemo;
    }
  }, [
    currentStatus,
    currentPoints,
    currentMemo,
    submissionLink,
    submissionMessage,
    initialIsDeleted,
    initialIsImageDeleted,
    submissionImagePath
  ]);

  useEffect(() => {
    return () => {
      if (pendingReplacementImage?.previewUrl) {
        URL.revokeObjectURL(pendingReplacementImage.previewUrl);
      }
    };
  }, [pendingReplacementImage?.previewUrl]);

  useEffect(() => {
    if (!isOpen || !submissionFeaturesEnabled || assignmentType === 'routine' || !submissionImagePath) {
      setSubmissionImageUrl(null);
      setSubmissionImageLoadFailed(false);
      return;
    }
    let cancelled = false;
    setSubmissionImageLoadFailed(false);
    assignmentSubmissionImageCache
      .getObjectUrl(submissionImagePath, submissionImageUpdatedAt)
      .then((url) => {
        if (cancelled) return;
        if (url) {
          setSubmissionImageUrl(url);
          setSubmissionImageLoadFailed(false);
        } else {
          setSubmissionImageUrl(null);
          setSubmissionImageLoadFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    submissionFeaturesEnabled,
    assignmentType,
    submissionImagePath,
    submissionImageUpdatedAt
  ]);

  // 상태 변경 시 포인트 자동 계산
  useEffect(() => {
    if (isForcedPoints) return;
    let calculatedPoints = 0;
    
    if (status === 'pending') {
      calculatedPoints = 0;
    } else if (status === 'confirmed') {
      // 과제 설정 포인트
      calculatedPoints = assignmentBasePoints || 0;
    } else if (status === 'delayed') {
      // 지연확인: 확인 포인트의 설정된 백분율 (반올림)
      calculatedPoints = getPointsByConfirmedPercent(assignmentBasePoints || 0, statusDefaultPoints?.delayed ?? 0);
    } else if (status === 'exempted') {
      // 예외: 확인 포인트의 설정된 백분율 (반올림)
      calculatedPoints = getPointsByConfirmedPercent(assignmentBasePoints || 0, statusDefaultPoints?.exempted ?? 0);
    }
    
    setPoints(calculatedPoints);
  }, [status, assignmentBasePoints, statusDefaultPoints, isForcedPoints]);

  const statusOptions = React.useMemo(
    () => [
      { value: 'pending', label: '미확인', bgColor: 'var(--status-pending)' }, // 미확인
      { value: 'confirmed', label: '확인', bgColor: 'var(--status-confirmed)' }, // 확인
      { value: 'delayed', label: '지연확인', bgColor: 'var(--status-delayed)' }, // 지연확인
      { value: 'exempted', label: '예외', bgColor: 'var(--status-exempted)' }  // 예외
    ],
    []
  );

  const handleToggleSubmissionDelete = () => {
    setIsSubmissionDeleted((prev) => {
      const next = !prev;
      if (next) setIsEditingSubmission(false);
      return next;
    });
  };

  const handleCancelEditSubmission = () => {
    setEditedSubmissionLink(submissionLink || '');
    setEditedSubmissionMessage(submissionMessage || '');
    if (pendingReplacementImage?.previewUrl) {
      URL.revokeObjectURL(pendingReplacementImage.previewUrl);
    }
    setPendingReplacementImage(null);
    setIsEditingSubmission(false);
  };

  const handleReplacementImageSave = (blob: Blob, mimeType: string) => {
    if (pendingReplacementImage?.previewUrl) {
      URL.revokeObjectURL(pendingReplacementImage.previewUrl);
    }
    setPendingReplacementImage({
      blob,
      mimeType,
      previewUrl: URL.createObjectURL(blob)
    });
    setIsSubmissionDeleted(false);
    setIsSubmissionCropOpen(false);
  };

  const handleSave = () => {
    const hasReplacement = !!pendingReplacementImage;
    const imageDeleteRequested =
      isSubmissionDeleted && !!submissionImagePath && !hasReplacement;
    const linkMessageChanged =
      editedSubmissionLink !== (submissionLink || '') ||
      editedSubmissionMessage !== (submissionMessage || '') ||
      isSubmissionDeleted !== (initialIsDeleted || initialIsImageDeleted);
    const hasSubmissionContent =
      !!(submissionLink || submissionMessage || submissionImagePath || editedSubmissionLink?.trim() ||
        editedSubmissionMessage?.trim() || hasReplacement);

    if (
      showSubmissionSection &&
      hasSubmissionContent &&
      (linkMessageChanged || imageDeleteRequested || hasReplacement)
    ) {
      onSave(status, points, memo, {
        link: editedSubmissionLink,
        message: editedSubmissionMessage,
        isDeleted: isSubmissionDeleted,
        isImageDeleted: imageDeleteRequested,
        replacementImage: pendingReplacementImage
          ? { blob: pendingReplacementImage.blob, mimeType: pendingReplacementImage.mimeType }
          : undefined
      });
    } else {
      onSave(status, points, memo);
    }
    onClose();
  };

  // 키보드 단축키로 과제 상태 선택
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      // 입력 필드에 포커스가 있을 때는 단축키 비활성화
      const activeElement = document.activeElement;
      if (activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement).contentEditable === 'true'
      )) {
        return;
      }
      
      // 숫자 키 1,2,3,4로 과제 상태 선택 (항상 해당 상태에 따른 포인트 적용)
      if (event.key >= '1' && event.key <= '4') {
        event.preventDefault();
        const index = parseInt(event.key) - 1;
        if (index >= 0 && index < statusOptions.length) {
          const selectedOption = statusOptions[index];
          setIsForcedPoints(false);
          setStatus(selectedOption.value);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, statusOptions, assignmentBasePoints]);

  const hasSubmissionLinkOrMessage =
    !!(submissionLink || submissionMessage || editedSubmissionLink?.trim() || editedSubmissionMessage?.trim());
  const showSubmissionSection = submissionFeaturesEnabled && assignmentType !== 'routine';
  const hasAnySubmissionContent =
    !!submissionImagePath || !!pendingReplacementImage || hasSubmissionLinkOrMessage;
  const displayImageUrl = pendingReplacementImage?.previewUrl || submissionImageUrl;
  const showImageBlock =
    submissionFeaturesEnabled &&
    assignmentType !== 'routine' &&
    (!!submissionImagePath || !!pendingReplacementImage || isEditingSubmission);

  const getNavigatePayload = () => ({
    status,
    points,
    memo,
    submissionLink: editedSubmissionLink,
    submissionMessage: editedSubmissionMessage,
    isDeleted: isSubmissionDeleted,
    isImageDeleted: isSubmissionDeleted
  });

  const handleBackdropNavigate = (
    e: React.MouseEvent,
    direction: 'prev' | 'next'
  ) => {
    e.stopPropagation();
    if (direction === 'prev' && !canNavigatePrev) return;
    if (direction === 'next' && !canNavigateNext) return;
    onNavigate?.(direction, getNavigatePayload());
  };

  const handleBackdropWheel = (e: React.WheelEvent<HTMLButtonElement>) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.scrollTop += e.deltaY;
  };

  const backdropChevronPrev = (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M15 18l-6-6 6-6"
        stroke="#fff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const backdropChevronNext = (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M9 18l6-6-6-6"
        stroke="#fff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const overlayCloseIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const trashIconSvg = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  if (!isOpen) return null;

  return (
    <Overlay ref={overlayRef} $z={overlayZ}>
      <OverlayCloseButton type="button" aria-label="닫기" onClick={onClose}>
        {overlayCloseIcon}
      </OverlayCloseButton>
      <BackdropNavZoneShell $side="left">
        <BackdropNavHitArea
          type="button"
          $side="left"
          aria-label="이전 멤버"
          disabled={!canNavigatePrev}
          onClick={(e) => handleBackdropNavigate(e, 'prev')}
          onWheel={handleBackdropWheel}
        >
          {backdropChevronPrev}
        </BackdropNavHitArea>
      </BackdropNavZoneShell>
      <BackdropNavZoneShell $side="right">
        <BackdropNavHitArea
          type="button"
          $side="right"
          aria-label="다음 멤버"
          disabled={!canNavigateNext}
          onClick={(e) => handleBackdropNavigate(e, 'next')}
          onWheel={handleBackdropWheel}
        >
          {backdropChevronNext}
        </BackdropNavHitArea>
      </BackdropNavZoneShell>
      <OverlayScroll>
        <Container
          $z={containerZ}
          ref={containerRef}
          onClick={(e) => e.stopPropagation()}
        >
        <Header>
          <TitleContainer>
            <Title>
              {studentNumber}. {studentName}
            </Title>
          </TitleContainer>
          <NavigationContainer>
            <Navigation>
              <NavButton 
                type="button" 
                aria-label="이전 멤버"
                onClick={() => onNavigate?.('prev', getNavigatePayload())}
                disabled={!canNavigatePrev}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </NavButton>
              <NavButton 
                type="button" 
                aria-label="다음 멤버"
                onClick={() => onNavigate?.('next', getNavigatePayload())}
                disabled={!canNavigateNext}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </NavButton>
            </Navigation>
          </NavigationContainer>
        </Header>
        
        <Content>
          <AssignmentInfo>
            {assignmentType === 'project' && projectTitle ? (
              <>
                <AssignmentTitle>{projectTitle}</AssignmentTitle>
                {subTaskTitle && <SubInfo>{subTaskTitle}</SubInfo>}
              </>
            ) : assignmentType === 'routine' && routineTitle ? (
              <>
                <AssignmentTitle>{routineTitle}</AssignmentTitle>
                {dateId && <SubInfo>{dateId}</SubInfo>}
              </>
            ) : (
              <>
                <AssignmentTitle>{assignmentTitle}</AssignmentTitle>
              </>
            )}
          </AssignmentInfo>

          {showSubmissionSection && (
            <SubmissionSection>
              <SubmissionHeader>
                <SubmissionTitleWrapper>
                  <SubmissionSectionTitle>제출 확인</SubmissionSectionTitle>
                </SubmissionTitleWrapper>
                <SubmissionActionButtons>
                  <SubmissionActionButton
                    type="button"
                    title="수정"
                    disabled={isSubmissionDeleted}
                    onClick={() => setIsEditingSubmission(!isEditingSubmission)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </SubmissionActionButton>
                  {hasAnySubmissionContent && (
                    <SubmissionActionButton
                      type="button"
                      title="삭제"
                      disabled={isEditingSubmission}
                      onClick={handleToggleSubmissionDelete}
                    >
                      {trashIconSvg}
                    </SubmissionActionButton>
                  )}
                </SubmissionActionButtons>
              </SubmissionHeader>

              {isEditingSubmission ? (
                <SubmissionEditForm>
                  {showImageBlock && (
                    <SubmissionImageEditBlock>
                      {displayImageUrl ? (
                        <SubmissionImagePreviewWrap>
                          <SubmissionImagePreview src={displayImageUrl} alt="과제 제출 사진" />
                          {isSubmissionDeleted && submissionImagePath && !pendingReplacementImage && (
                            <SubmissionDeleteOverlay>삭제 예정</SubmissionDeleteOverlay>
                          )}
                        </SubmissionImagePreviewWrap>
                      ) : submissionImagePath && submissionImageLoadFailed ? (
                        <SubmissionImagePlaceholder>이미지를 불러올 수 없습니다.</SubmissionImagePlaceholder>
                      ) : submissionImagePath && !displayImageUrl ? (
                        <SubmissionImagePlaceholder>이미지를 불러오는 중…</SubmissionImagePlaceholder>
                      ) : null}
                      <SubmissionImagePickBtn type="button" onClick={() => setIsSubmissionCropOpen(true)}>
                        {displayImageUrl || submissionImagePath ? '사진 변경' : '사진 추가'}
                      </SubmissionImagePickBtn>
                    </SubmissionImageEditBlock>
                  )}
                  <SubmissionEditInput
                    type="text"
                    placeholder="링크 URL"
                    value={editedSubmissionLink}
                    onChange={(e) => setEditedSubmissionLink(e.target.value)}
                  />
                  <SubmissionEditTextarea
                    placeholder="메시지"
                    value={editedSubmissionMessage}
                    onChange={(e) => setEditedSubmissionMessage(e.target.value)}
                  />
                  <SubmissionEditActions>
                    <SubmissionEditButton type="button" onClick={handleCancelEditSubmission}>
                      취소
                    </SubmissionEditButton>
                    <SubmissionEditButton type="button" $primary onClick={() => setIsEditingSubmission(false)}>
                      수정
                    </SubmissionEditButton>
                  </SubmissionEditActions>
                </SubmissionEditForm>
              ) : (
                <>
                  {submissionImagePath || pendingReplacementImage ? (
                    <SubmissionImageRow>
                      <SubmissionImagePreviewWrap>
                        {displayImageUrl ? (
                          <SubmissionImagePreview src={displayImageUrl} alt="과제 제출 사진" />
                        ) : submissionImageLoadFailed ? (
                          <SubmissionImagePlaceholder>이미지를 불러올 수 없습니다.</SubmissionImagePlaceholder>
                        ) : (
                          <SubmissionImagePlaceholder>이미지를 불러오는 중…</SubmissionImagePlaceholder>
                        )}
                        {isSubmissionDeleted && submissionImagePath && (
                          <SubmissionDeleteOverlay>삭제 예정</SubmissionDeleteOverlay>
                        )}
                      </SubmissionImagePreviewWrap>
                    </SubmissionImageRow>
                  ) : (
                    <SubmissionEmptyImageSlot>멤버가 제출한 이미지가 없습니다.</SubmissionEmptyImageSlot>
                  )}

                  {hasSubmissionLinkOrMessage ? (
                    editedSubmissionLink ? (
                      <SubmissionContentWrap>
                        <SubmissionLinkButton
                          as={isSubmissionDeleted ? 'div' : 'a'}
                          href={
                            isSubmissionDeleted
                              ? undefined
                              : editedSubmissionLink.startsWith('http://') ||
                                  editedSubmissionLink.startsWith('https://')
                                ? editedSubmissionLink
                                : `https://${editedSubmissionLink}`
                          }
                          target={isSubmissionDeleted ? undefined : '_blank'}
                          rel={isSubmissionDeleted ? undefined : 'noopener noreferrer'}
                          style={isSubmissionDeleted ? { pointerEvents: 'none', cursor: 'default' } : undefined}
                        >
                          <SubmissionButtonContent>
                            <LinkIcon>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </LinkIcon>
                            {editedSubmissionMessage || '제출된 과제 링크 보기'}
                          </SubmissionButtonContent>
                        </SubmissionLinkButton>
                        {isSubmissionDeleted && <SubmissionDeleteOverlay>삭제 예정</SubmissionDeleteOverlay>}
                      </SubmissionContentWrap>
                    ) : (
                      <SubmissionContentWrap>
                        <SubmissionMessageButton
                          onClick={(e) => {
                            e.preventDefault();
                          }}
                        >
                          {editedSubmissionMessage || '제출된 메시지'}
                        </SubmissionMessageButton>
                        {isSubmissionDeleted && <SubmissionDeleteOverlay>삭제 예정</SubmissionDeleteOverlay>}
                      </SubmissionContentWrap>
                    )
                  ) : (
                    <SubmissionEmptyLinkSlot>멤버가 제출한 링크 및 메세지가 없습니다.</SubmissionEmptyLinkSlot>
                  )}
                </>
              )}
            </SubmissionSection>
          )}

          <AssignmentSubmissionCropModal
            isOpen={isSubmissionCropOpen}
            onClose={() => setIsSubmissionCropOpen(false)}
            onSave={handleReplacementImageSave}
          />

          <StatusButtonsRow>
            {statusOptions.map((option, index) => (
              <StatusButton
                key={option.value}
                $isSelected={status === option.value}
                onClick={() => {
                  setIsForcedPoints(false);
                  setStatus(option.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // 포인트 필드로 포커스 이동
                    const pointsField = document.querySelector('.BatchInput') as HTMLInputElement;
                    if (pointsField) {
                      pointsField.focus();
                    }
                  }
                }}
              >
                <StatusButtonColorDot 
                  $isSelected={status === option.value}
                  style={{ 
                    backgroundColor: status === option.value ? option.bgColor : 'var(--color-border)'
                  } as React.CSSProperties}
                >
                  {index + 1}
                </StatusButtonColorDot>
                <StatusButtonText $isSelected={status === option.value}>
                  {option.label}
                </StatusButtonText>
              </StatusButton>
            ))}
          </StatusButtonsRow>
          
          <BatchInputGroup>
            <BatchLabel>포인트</BatchLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <PointsMinusBtn 
                type="button" 
                aria-label="포인트 빼기"
                onClick={() => setPoints(Math.max(0, points - defaultPoints))}
              >
                <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </PointsMinusBtn>
              <div style={{ flex: 1 }}>
                <BatchInput
                  className="BatchInput"
                  type="text"
                  value={points.toString()}
                  onChange={(e) => setPoints(parseInt(e.target.value) || 0)}
                  onFocus={(e) => e.target.select()}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      // 메모 필드로 포커스 이동
                      const memoField = document.querySelector('.MemoEditTextarea') as HTMLTextAreaElement;
                      if (memoField) {
                        memoField.focus();
                      }
                    }
                  }}
                />
              </div>
              <PointsPlusBtn 
                type="button" 
                aria-label="포인트 더하기"
                onClick={() => setPoints(points + defaultPoints)}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </PointsPlusBtn>
            </div>
          </BatchInputGroup>
          
          <MemoEditTextarea
            className="MemoEditTextarea"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="메모를 입력하세요..."
            rows={4}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                // 포커스 제거 (메모 입력 완료)
                (e.target as HTMLTextAreaElement).blur();
              }
            }}
          />
        </Content>
        
        <Footer>
          <Button data-variant="modal-secondary" onClick={onClose}>닫기</Button>
          <SaveButton 
            onClick={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              }
            }}
          >
            확인
          </SaveButton>
        </Footer>
        </Container>
      </OverlayScroll>
    </Overlay>
  );
};

export default StudentDetailModal;
